import React, { useState, useEffect, useRef } from 'react';
import { ASSETS } from '../assetManifest';

const BASE_Z_INDEX = -2000;

const Sprite = React.forwardRef(({
  emoji,
  imageUrl,
  size = 40,
  style = {},
  className = '',
  ...props
}, ref) => {
  if (imageUrl) {
    return (
      <div
        ref={ref}
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          ...style
        }}
        {...props}
      />
    );
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{
        fontSize: `${size}px`,
        lineHeight: 1,
        height: `${size}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      }}
      {...props}
    >
      {emoji}
    </div>
  );
});

Sprite.displayName = 'Sprite';

const Shadow = ({ width = 20, height = 6 }) => (
  <div
    style={{
      position: 'absolute',
      left: '50%',
      top: '100%',
      width: `${width}px`,
      height: `${height}px`,
      backgroundColor: 'rgba(0,0,0,0.3)',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)'
    }}
  />
);

function GameStage({ gameState, gameInterface }) {
  const [tick, setTick] = useState(0);
  const worldCenterX = gameState.worldSize.width / 2;
  const worldCenterY = gameState.worldSize.height / 2;
  const [camera, setCamera] = useState({ 
    x: worldCenterX - gameState.screenSize.width / 2,
    y: worldCenterY - gameState.screenSize.height / 2
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 10);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const currentPlayer = gameState.entities[gameInterface.userId];
    if (currentPlayer && typeof currentPlayer.x === 'number' && typeof currentPlayer.y === 'number') {
      let targetX = currentPlayer.x - gameState.screenSize.width / 2;
      let targetY = currentPlayer.y - gameState.screenSize.height / 2;
      
      targetX = Math.max(0, Math.min(targetX, gameState.worldSize.width - gameState.screenSize.width));
      targetY = Math.max(0, Math.min(targetY, gameState.worldSize.height - gameState.screenSize.height));
      
      setCamera(prev => ({
        x: Math.max(0, Math.min(prev.x + (targetX - prev.x) * 0.1, gameState.worldSize.width - gameState.screenSize.width)),
        y: Math.max(0, Math.min(prev.y + (targetY - prev.y) * 0.1, gameState.worldSize.height - gameState.screenSize.height))
      }));
    }
  }, [tick, gameState.entities, gameInterface.userId, gameState.screenSize.width, gameState.screenSize.height, gameState.worldSize.width, gameState.worldSize.height]);

  const isInView = (x, y, margin = 100) => {
    if (typeof x !== 'number' || typeof y !== 'number') return false;
    const screenX = x - camera.x;
    const screenY = y - camera.y;
    return screenX >= -margin &&
           screenX <= gameState.screenSize.width + margin &&
           screenY >= -margin &&
           screenY <= gameState.screenSize.height + margin;
  };

  return (
    <div className="h-full w-full relative" style={{ transform: 'translateZ(0)' }}>
      <div
        className="h-full w-full absolute"
        style={{ 
          zIndex: BASE_Z_INDEX,
          userSelect: 'none',
          backgroundImage: `url(${ASSETS.backgrounds.winter.path})`,
          backgroundSize: 'cover',
          backgroundPosition: `${-camera.x}px ${-camera.y}px`,
          backgroundRepeat: 'repeat'
        }}
      />

      {/* Render resource gain effects */}
      {gameState.resourceGainEffects.map((effect, index) => {
        if (!isInView(effect.x, effect.y)) return null;
        const screenX = effect.x - camera.x;
        const screenY = effect.y - camera.y;
        const opacity = effect.timer / 1000;

        return (
          <div
            key={`effect-${index}`}
            style={{
              position: 'absolute',
              left: screenX,
              top: screenY,
              transform: 'translate(-50%, -50%)',
              zIndex: BASE_Z_INDEX + 1000,
              opacity,
              transition: 'opacity 0.1s'
            }}
          >
            <div className="flex items-center">
              <img 
                src={effect.type === 'wood' ? ASSETS.items.wood.path : ASSETS.items.fruit.path}
                alt={effect.type}
                style={{ width: '24px', height: '24px' }}
              />
              <span className="text-white text-xl font-bold" style={{ textShadow: '2px 2px 0 #000' }}>
                +1
              </span>
            </div>
          </div>
        );
      })}

      {Object.values(gameState.entities).map((entity) => {
        if (!entity || typeof entity.x !== 'number' || typeof entity.y !== 'number' || !isInView(entity.x, entity.y)) return null;

        const screenX = entity.x - camera.x;
        const screenY = entity.y - camera.y;

        return (
          <div
            key={entity.id}
            style={{
              position: 'absolute',
              left: screenX,
              top: screenY,
              transform: 'translate(-50%, -50%)',
              zIndex: Math.floor(entity.y) + BASE_Z_INDEX + 2,
            }}
          >
            <div className="flex flex-col items-center gap-1">
              {entity.type === 'player' && (
                <>
                  <div style={{ 
                    color: entity.id === gameInterface.userId ? '#FA2686' : '#0955CB', 
                    marginBottom: '4px', 
                    fontWeight: 'bold' 
                  }}>
                    {entity.name}
                    {entity.isDead && ' (Dead)'}
                    {entity.state === 'gathering' && (
                      <div style={{ color: '#38A5FE', fontSize: '0.9em' }}>
                        (Gathering...)
                      </div>
                    )}
                  </div>
                  <div style={{ transform: `scaleX(${entity.direction === 'left' ? -1 : 1})` }}>
                    <Sprite
                      imageUrl={ASSETS.characters.player.path}
                      size={40}
                      className={entity.state === 'walking' ? 'animate-bounce' : ''}
                      style={{ opacity: entity.isDead ? 0.5 : 1 }}
                    />
                  </div>
                </>
              )}
              
              {entity.type === 'bush' && (
                <>
                  <Sprite 
                    imageUrl={ASSETS.environment.bush.path}
                    size={40}
                  />
                  <div className="text-sm flex items-center h-[24px]" style={{
                    background: 'rgba(54,123,250,0.40)',
                    border: '1px solid #0963BD',
                    borderRadius: '3px',
                    padding: '0 8px',
                    color: '#090E45'
                  }}>
                    <img src={ASSETS.items.fruit.path} alt="Fruit" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                    {entity.fruits}
                  </div>
                </>
              )}
              
              {entity.type === 'tree' && (
                <>
                  <Sprite 
                    imageUrl={ASSETS.environment.tree.path}
                    size={40}
                  />
                  <div className="text-sm flex items-center h-[24px]" style={{
                    background: 'rgba(54,123,250,0.40)',
                    border: '1px solid #0963BD',
                    borderRadius: '3px',
                    padding: '0 8px',
                    color: '#090E45'
                  }}>
                    <img src={ASSETS.items.wood.path} alt="Wood" style={{ width: '16px', height: '16px', marginRight: '4px' }} />
                    {entity.logs}
                  </div>
                </>
              )}
              
              {entity.type === 'fire' && (
                <>
                  <div 
                    style={{
                      position: 'absolute',
                      width: '200px',
                      height: '200px',
                      backgroundColor: 'rgba(255, 100, 0, 0.1)',
                      border: '2px solid rgba(255, 100, 0, 0.2)',
                      borderRadius: '50%',
                      zIndex: BASE_Z_INDEX + 1
                    }}
                  />
                  <div className="text-white text-sm bg-black bg-opacity-50 px-2 rounded mb-2">
                    {Math.ceil(entity.timeRemaining / 1000)}s
                  </div>
                  <Sprite emoji="🔥" size={40} />
                </>
              )}
              
              <Shadow />
            </div>
          </div>
        );
      })}

      {gameState.notification && (
        <div
          className="absolute top-1/4 left-1/2 transform -translate-x-1/2 flex flex-col items-center justify-center"
          style={{ 
            zIndex: BASE_Z_INDEX + 1000,
            backgroundImage: `url(${ASSETS.ui.popup.path})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            width: '300px',
            height: '187px',
            marginTop: '120px',
            fontFamily: 'Asap',
            fontWeight: 'bold'
          }}
        >
          <span style={{ color: '#2A4163', marginTop: '60px', fontSize: '1.125rem' }}>The cold intensifies!</span>
          <span style={{ color: 'white', fontSize: '1.25rem' }}>Cold damage is now </span>
          <span style={{ color: '#53DAFF', fontSize: '1.5rem' }}>{gameState.coldIntensity}x</span>
        </div>
      )}
    </div>
  );
}

export default GameStage;
